import { useState, useEffect, useCallback, useContext } from "react";

import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  IconButton,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { postSurveyCreate } from "@rubi/api-generated/clientRs/sdk.gen";
import type { SurveyVersionOverview } from "@rubi/api-generated/clientRs/types.gen";
import { toast } from "@rubi/ui";

import { devEnvironment } from "@/common/featureFlags";
import { RUBICON_ADMIN_ORG_IDS } from "@/common/serviceConfigurations";
import { useFormBuilderContext } from "@/components/online-reporting/form-management/FormContextProvider";
import {
  extractLocaleStringFromField,
  formatVersionNumber,
} from "@/components/online-reporting/form-management/utils";
import { ILocaleObject } from "@/components/online-reporting/types";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useServiceConfigurations } from "@/hooks/useCommunities";

interface OrgOption {
  id: string;
  name: string;
}

type ViewMode = "select" | "copying" | "results";

interface CopyResult {
  surveyId: string;
  surveyName: string;
  success: boolean;
  error?: string;
  newSurveyId?: string;
}

interface CopyReportsModalProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  onCopySuccess: () => void;
}

/**
 * Appends a suffix to a title, handling both string and locale object formats.
 * For same-org copies: "(Copy)"
 * For cross-org copies: "(Source Org Name)"
 */
const appendTitleSuffix = (
  title: string | ILocaleObject | undefined,
  suffix: string,
): string | ILocaleObject => {
  const formattedSuffix = `(${suffix})`;

  if (!title) {
    return `Untitled Survey ${formattedSuffix}`;
  }

  if (typeof title === "string") {
    return `${title} ${formattedSuffix}`;
  }

  // Handle locale object
  const result: ILocaleObject = {
    default: `${title.default} ${formattedSuffix}`,
  };
  if (title.fr) {
    // For French, use "Copie" if it's a same-org copy, otherwise use the org name
    const frSuffix = suffix === "Copy" ? "Copie" : suffix;
    result.fr = `${title.fr} (${frSuffix})`;
  }
  return result;
};

/**
 * Gets the survey title from a SurveyVersionOverview
 */
const getSurveyTitle = (survey: SurveyVersionOverview): string => {
  const version = survey.active_version || survey.draft_version;
  if (!version) return "Untitled Survey";

  const config = version.configuration as { title?: string | ILocaleObject };
  const title = config?.title;

  if (title) {
    return (
      extractLocaleStringFromField(title as string | ILocaleObject) ||
      "Untitled Survey"
    );
  }
  return "Untitled Survey";
};

export const CopyReportsModal: React.FC<CopyReportsModalProps> = ({
  open,
  onClose,
  orgId,
  onCopySuccess,
}) => {
  const { currentUser } = useContext(AuthStateContext);
  const { surveysWithActiveVersions } = useFormBuilderContext();
  const serviceConfigurations = useServiceConfigurations();

  const [viewMode, setViewMode] = useState<ViewMode>("select");

  const [organizations, setOrganizations] = useState<OrgOption[]>([]);
  const [selectedSurveyIds, setSelectedSurveyIds] = useState<Set<string>>(
    new Set(),
  );
  const [destinationOrgId, setDestinationOrgId] = useState<string>("");

  const [copyProgress, setCopyProgress] = useState({ completed: 0, total: 0 });
  const [copyResults, setCopyResults] = useState<CopyResult[]>([]);

  // Check if user is from a Rubicon admin org (same logic as "Viewing as:" dropdown in AppBar)
  const canAccessAllOrgs =
    currentUser?.rubi_org_id &&
    RUBICON_ADMIN_ORG_IDS.includes(currentUser.rubi_org_id);

  const isDev = devEnvironment();

  // Build organizations list from serviceConfigurations (same as "Viewing as:" dropdown)
  const loadOrganizations = useCallback(() => {
    if (!canAccessAllOrgs) {
      // Non-Rubicon users can only copy to current org
      return;
    }

    const orgs: OrgOption[] = Object.values(serviceConfigurations)
      .filter((cfg) => cfg.id) // Only include orgs with valid IDs
      .map((cfg) => ({
        id: isDev ? cfg.testID : cfg.id,
        name: cfg.fullName ?? cfg.shortName ?? "Unknown",
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    setOrganizations(orgs);
  }, [canAccessAllOrgs, isDev, serviceConfigurations]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setViewMode("select");
      setSelectedSurveyIds(new Set());
      setDestinationOrgId(orgId); // Default to current org
      setCopyProgress({ completed: 0, total: 0 });
      setCopyResults([]);
      loadOrganizations();
    }
  }, [open, orgId, loadOrganizations]);

  const handleToggleSelection = (surveyId: string) => {
    const newSelection = new Set(selectedSurveyIds);
    if (newSelection.has(surveyId)) {
      newSelection.delete(surveyId);
    } else {
      newSelection.add(surveyId);
    }
    setSelectedSurveyIds(newSelection);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSurveyIds(
        new Set(surveysWithActiveVersions.map((s) => s.parent_survey_id)),
      );
    } else {
      setSelectedSurveyIds(new Set());
    }
  };

  const handleCopy = async () => {
    if (selectedSurveyIds.size === 0) return;

    const selectedSurveys = surveysWithActiveVersions.filter((s) =>
      selectedSurveyIds.has(s.parent_survey_id),
    );

    setViewMode("copying");
    setCopyProgress({ completed: 0, total: selectedSurveys.length });
    setCopyResults([]);

    // Determine the suffix for copied surveys
    // Same org: "(Copy)", Different org: "(Source Org Name)"
    const isSameOrg = destinationOrgId === orgId;
    let titleSuffix = "Copy";
    if (!isSameOrg) {
      // Get the source org name from serviceConfigurations
      const sourceOrgConfig = Object.values(serviceConfigurations).find(
        (cfg) => (isDev ? cfg.testID : cfg.id) === orgId,
      );
      titleSuffix = sourceOrgConfig?.fullName ?? "Copy";
    }

    const results: CopyResult[] = [];

    for (let i = 0; i < selectedSurveys.length; i++) {
      const survey = selectedSurveys[i];
      const surveyName = getSurveyTitle(survey);

      try {
        // Copy the active version if available, otherwise use draft
        const versionToCopy = survey.active_version || survey.draft_version;

        if (!versionToCopy) {
          results.push({
            surveyId: survey.parent_survey_id,
            surveyName,
            success: false,
            error: "No version available to copy",
          });
          setCopyResults([...results]);
          setCopyProgress({ completed: i + 1, total: selectedSurveys.length });
          continue;
        }

        // Get the configuration and append suffix to the title
        const originalConfig = versionToCopy.configuration as {
          title?: string | ILocaleObject;
          [key: string]: unknown;
        };
        const originalPdfConfig = versionToCopy.pdf_configuration as {
          title?: string | ILocaleObject;
          [key: string]: unknown;
        };

        const newConfig = {
          ...originalConfig,
          title: appendTitleSuffix(originalConfig?.title, titleSuffix),
        };

        const newPdfConfig = {
          ...originalPdfConfig,
          title: appendTitleSuffix(originalPdfConfig?.title, titleSuffix),
        };

        const response = await postSurveyCreate({
          path: { org_id: destinationOrgId },
          body: {
            configuration: newConfig,
            pdf_configuration: newPdfConfig,
          },
        });

        const newSurvey = response.data?.survey;

        results.push({
          surveyId: survey.parent_survey_id,
          surveyName,
          success: true,
          newSurveyId: newSurvey?.parent_survey_id,
        });
      } catch (error: any) {
        console.error(
          `Error copying survey ${survey.parent_survey_id}:`,
          error,
        );
        const errorMessage =
          error?.response?.data?.console_msg || "Failed to copy survey";
        results.push({
          surveyId: survey.parent_survey_id,
          surveyName,
          success: false,
          error: errorMessage,
        });
      }

      setCopyResults([...results]);
      setCopyProgress({ completed: i + 1, total: selectedSurveys.length });
    }

    setViewMode("results");

    const successCount = results.filter((r) => r.success).length;
    const failCount = results.filter((r) => !r.success).length;

    if (failCount === 0) {
      toast.success(`Successfully copied ${successCount} survey(s)`);
    } else if (successCount > 0) {
      toast.warning(`Copied ${successCount} survey(s), ${failCount} failed`);
    } else {
      toast.error("Failed to copy surveys");
    }
  };

  const handleDone = () => {
    onCopySuccess();
    onClose();
  };

  // Get the current org name from serviceConfigurations
  const currentOrgConfig = Object.values(serviceConfigurations).find(
    (cfg) => (isDev ? cfg.testID : cfg.id) === orgId,
  );
  const currentOrgName =
    currentOrgConfig?.fullName ??
    currentOrgConfig?.shortName ??
    "Current Organization";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            <ContentCopyIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Copy Reports
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ minHeight: 400 }}>
        {/* Empty State */}
        {surveysWithActiveVersions.length === 0 && viewMode === "select" && (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height={300}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No surveys available to copy
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create some surveys first before copying.
            </Typography>
          </Box>
        )}

        {/* Select View */}
        {surveysWithActiveVersions.length > 0 && viewMode === "select" && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">
                {surveysWithActiveVersions.length} Survey
                {surveysWithActiveVersions.length !== 1 ? "s" : ""} Available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedSurveyIds.size} of {surveysWithActiveVersions.length}{" "}
                selected
              </Typography>
            </Box>

            <TableContainer sx={{ maxHeight: 300 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selectedSurveyIds.size > 0 &&
                          selectedSurveyIds.size <
                            surveysWithActiveVersions.length
                        }
                        checked={
                          surveysWithActiveVersions.length > 0 &&
                          selectedSurveyIds.size ===
                            surveysWithActiveVersions.length
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>Survey Name</TableCell>
                    <TableCell>Version</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {surveysWithActiveVersions.map((survey) => (
                    <TableRow
                      key={survey.parent_survey_id}
                      hover
                      onClick={() =>
                        handleToggleSelection(survey.parent_survey_id)
                      }
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSurveyIds.has(
                            survey.parent_survey_id,
                          )}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() =>
                            handleToggleSelection(survey.parent_survey_id)
                          }
                        />
                      </TableCell>
                      <TableCell>{getSurveyTitle(survey)}</TableCell>
                      <TableCell>
                        {survey.active_version ? (
                          <Chip
                            label={`v${formatVersionNumber(survey.active_version)}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Chip label="Draft" size="small" color="warning" />
                        )}
                      </TableCell>
                      <TableCell>
                        {survey.active_version ? (
                          survey.is_published ? (
                            <Chip
                              label="Published"
                              size="small"
                              color="success"
                            />
                          ) : (
                            <Chip label="Active" size="small" color="info" />
                          )
                        ) : (
                          <Chip
                            label="Draft Only"
                            size="small"
                            color="warning"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Destination Org Selection */}
            <Box sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Destination
              </Typography>

              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>Destination Organization</InputLabel>
                <Select
                  value={destinationOrgId}
                  label="Destination Organization"
                  onChange={(e) => setDestinationOrgId(e.target.value)}
                >
                  {canAccessAllOrgs && organizations.length > 0 ? (
                    organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                        {org.id === orgId && " (Current)"}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value={orgId}>{currentOrgName}</MenuItem>
                  )}
                </Select>
              </FormControl>

              <Alert severity="info" sx={{ mt: 2 }}>
                Copied surveys will be created as new drafts
                {destinationOrgId === orgId
                  ? " in this organization"
                  : " in the selected organization"}
                . Survey titles will have &quot;(Copy)&quot; appended.
              </Alert>
            </Box>
          </Box>
        )}

        {/* Copying View */}
        {viewMode === "copying" && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Copying Surveys...
            </Typography>

            <Box sx={{ mb: 3 }}>
              <LinearProgress
                variant="determinate"
                value={(copyProgress.completed / copyProgress.total) * 100}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, textAlign: "center" }}
              >
                Copying survey {copyProgress.completed} of {copyProgress.total}
                ...
              </Typography>
            </Box>

            {copyResults.length > 0 && (
              <TableContainer sx={{ maxHeight: 250 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Survey</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {copyResults.map((result) => (
                      <TableRow key={result.surveyId}>
                        <TableCell>{result.surveyName}</TableCell>
                        <TableCell>
                          {result.success ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Copied"
                              color="success"
                              size="small"
                            />
                          ) : (
                            <Chip
                              icon={<ErrorIcon />}
                              label="Failed"
                              color="error"
                              size="small"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Results View */}
        {viewMode === "results" && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Copy Results
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${copyResults.filter((r) => r.success).length} Succeeded`}
                color="success"
              />
              <Chip
                icon={<ErrorIcon />}
                label={`${copyResults.filter((r) => !r.success).length} Failed`}
                color={
                  copyResults.filter((r) => !r.success).length > 0
                    ? "error"
                    : "default"
                }
              />
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Survey Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {copyResults.map((result) => (
                    <TableRow key={result.surveyId}>
                      <TableCell>{result.surveyName}</TableCell>
                      <TableCell>
                        {result.success ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Success"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<ErrorIcon />}
                            label="Failed"
                            color="error"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {result.error ? (
                          <Typography variant="body2" color="error">
                            {result.error}
                          </Typography>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {viewMode === "select" && surveysWithActiveVersions.length > 0 && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCopy}
              disabled={selectedSurveyIds.size === 0}
              startIcon={<ContentCopyIcon />}
            >
              Copy {selectedSurveyIds.size} Survey
              {selectedSurveyIds.size !== 1 ? "s" : ""}
            </Button>
          </>
        )}

        {viewMode === "select" && surveysWithActiveVersions.length === 0 && (
          <Button onClick={onClose}>Close</Button>
        )}

        {viewMode === "copying" && (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Please wait...
          </Typography>
        )}

        {viewMode === "results" && (
          <Button variant="contained" onClick={handleDone}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
